<template>
  <div class="main-container">
    <loader v-if="loading"></loader>
    <div class="content">
      <div class="form-container">
        <div class="form">
          <div class="title-container">
            <div class="title">Log in to {{ systemName }}</div>
            <span v-if="envTag" class="env-tag">{{
              envTag.toUpperCase()
            }}</span>
          </div>
          <form v-if="loginState === 'email'" @submit="next" class="email-form">
            <div class="fields">
              <div class="field" :class="message && 'error'">
                <p class="label">Email address</p>
                <input
                  id="email"
                  v-model="email"
                  name="email"
                  class="input"
                  maxlength="100"
                  placeholder="Type here..."
                  :disabled="loading"
                />
              </div>
            </div>
            <div class="action">
              <button id="next-login-step" type="submit" @click="validateEmail">
                Next
              </button>
            </div>
          </form>
          <form v-if="loginState === 'password'" @submit="login">
            <div class="fields">
              <div class="field" :class="message && 'error'">
                <p class="label">Email</p>
                <div class="email-label disabled-label">
                  <label>{{ email }}</label>
                  <a class="change-link" @click="goBackToEmail">CHANGE</a>
                </div>
                <div class="password-row">
                  <p class="label">Password</p>
                  <router-link class="forgot-link" to="/forgot-password">
                    Forgot password?
                  </router-link>
                </div>
                <input
                  id="password"
                  v-model="password"
                  type="password"
                  name="password"
                  class="input"
                  maxlength="100"
                  :disabled="loading"
                />
              </div>
              <div class="action no-border">
                <button
                  id="signin"
                  :disabled="!password"
                  :class="!password ? 'disabled' : ''"
                  type="submit"
                >
                  Login
                </button>
              </div>
            </div>
            <template v-if="ssoMethods.length">
              <div class="divider">
                <span>OR</span>
              </div>
              <div class="method-buttons">
                <button
                  v-for="method in ssoMethods"
                  :key="method.provider"
                  @click="redirectToSSO(method)"
                  type="button"
                  class="secondary"
                  :disabled="loading"
                >
                  Login with {{ method.provider }}
                </button>
              </div>
            </template>
            <div class="action-separator"></div>
          </form>
          <Banner :message="message" v-if="message" />
          <div class="personal-data-text">
		Find out more about how Datamaran processes your
		Personal Data by reading our <a href="https://www.datamaran.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </div>
          <div class="personal-data-text">
		Find here our <a href="https://www.datamaran.com/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</a>
          </div>
	</div>
      </div>
      <LoginImage></LoginImage>
    </div>
  </div>
</template>
<script>
// @ is an alias to /src
import { Auth, Hub } from 'aws-amplify'

import Loader from '../components/Loader.vue'
import Banner from '../components/Banner.vue'
import LoginImage from '../components/LoginImage.vue'
import { required, email as emailValidator } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import {
  getAccessToken,
  getApplicationName,
  getRedirectUrl
} from '../helpers/redirection'
import { computed, ref, watchEffect } from 'vue'

export default {
  name: 'Login',
  components: {
    Loader,
    Banner,
    LoginImage
  },
  setup() {
    const envTag = window.location.hostname.split('.')[0].split('-')[1]
    const returnTo = new URL(window.location.href).searchParams.get('return_to')
    const email = ref(undefined)
    const password = ref(undefined)
    const message = ref(undefined)
    const loginState = ref('email')
    const loginMethods = ref([])
    const tenantUrl = ref(null)
    const loading = ref(true)
    const rules = computed(() => ({
      email: {
        required,
        emailValidator
      },
      password: { required }
    }))
    const v$ = useVuelidate(rules, { email, password })

    const ssoMethods = computed(() => loginMethods.value.filter(m => m.type === 'sso'))

    const oauthCode = new URLSearchParams(window.location.search).get('code')
    const ssoReturnTo = sessionStorage.getItem('sso_return_to')

    if (oauthCode && ssoReturnTo) {
      sessionStorage.removeItem('sso_return_to')
      Hub.listen('auth', async ({ payload }) => {
        if (payload.event === 'signIn') {
          try {
            const token = await getAccessToken()
            const url = new URL(ssoReturnTo)
            url.searchParams.append('code', token)
            window.location.href = url.toString()
          } catch (e) {
            console.error('SSO callback error:', e)
            message.value = 'SSO login failed. Please try again.'
            loading.value = false
          }
        } else if (payload.event === 'signIn_failure') {
          message.value = 'SSO login failed. Please try again.'
          loading.value = false
        }
      })
    } else {
      Auth.signOut()
      ;(async function checkAlreadyLoggedIn() {
        try {
          window.location.href = await getRedirectUrl(await getAccessToken())
        } catch (e) {
          console.error('Error while auto signing in ', { e })
          loading.value = false
        }
      })()
    }

    function goBackToEmail() {
      loginState.value = 'email'
      loginMethods.value = []
      tenantUrl.value = null
      message.value = undefined
    }

    function redirectToSSO(method) {
      if (!method.authorizeURL && !method.tenantURL) {
        message.value = 'SSO is not properly configured for this account. Please contact Support.'
        return
      }
      loading.value = true
      if (returnTo && method.authorizeURL && method.tenantURL) {
        sessionStorage.setItem('sso_return_to', method.tenantURL)
        const idpName = new URL(method.authorizeURL).searchParams.get('identity_provider')
        Auth.federatedSignIn({ customProvider: idpName })
        return
      }
      window.location.href = method.authorizeURL || method.tenantURL
    }

    async function login(e) {
      e.preventDefault()
      loading.value = true
      message.value = undefined
      try {
        await Auth.signIn(email.value, password.value)
        const token = await getAccessToken()
        window.location.href = await getRedirectUrl(token)
      } catch (e) {
        console.error('Error while signing in ', { e })
        message.value =
          'Please provide a valid email address and password. If you continue to have issues logging into your account, contact our Support team.'
        loading.value = false
      }
    }

    function handleMethods(methods) {
      if (methods.length === 1 && methods[0].type === 'sso') {
        if (methods[0].authorizeURL || methods[0].tenantURL) {
          redirectToSSO(methods[0])
          return true
        }
      }
      if (methods.length >= 1) {
        loginMethods.value = methods
        const pwMethod = methods.find(m => m.type === 'password')
        if (pwMethod) tenantUrl.value = pwMethod.tenantURL || null
      }
      return false
    }

    async function next(e) {
      e.preventDefault()
      loading.value = true
      message.value = undefined
      password.value = ''
      email.value = email.value.toLowerCase()
      try {
        const apiBase = process.env.VUE_APP_CHECK_PROVIDER_API || ''
        const checkUrl = `${apiBase}/api/login/check/${encodeURIComponent(email.value)}${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''}`
        const resp = await fetch(checkUrl)
        if (resp.ok && resp.headers.get('content-type')?.includes('application/json')) {
          const data = await resp.json()
          const methods = data.methods || []
          if (handleMethods(methods)) return
        }
      } catch (e) {
        console.error('Error checking provider ', { e })
      }
      loginState.value = 'password'
      loading.value = false
    }

    async function validateEmail(e) {
      if (!/^[\w.+-]+@([\w-]+\.)+[\w-]{2,63}$/.test(email.value)) {
        e.preventDefault()
        message.value =
          'Please provide a valid email address. If you continue to have issues logging into your account, contact our Support team.'
      }
    }
    watchEffect(() => {
      if (email.value && password.value) {
        message.value = undefined
      }
    })
    return {
      v$,
      email,
      password,
      loginState,
      loginMethods,
      ssoMethods,
      loading,
      message,
      login,
      next,
      goBackToEmail,
      redirectToSSO,
      envTag,
      systemName: getApplicationName(),
      validateEmail
    }
  }
}
</script>
<style>
.email-label {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 38px;
  padding-right: 60px;
}
.email-label > label {
  overflow: hidden;
  text-overflow: ellipsis;
}
.email-label > a {
  position: absolute;
  right: 10px;
  color: #007a88;
  font-weight: bold;
  font-size: 12px;
  font-family: TitilliumWeb-Regular;
}
.password-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.forgot-link {
  font-family: TitilliumWeb-Regular, sans-serif;
  font-size: 12px;
  color: #007a88;
}
.email-form .action {
  margin-top: 95px;
}
.form .action.no-border {
  border-bottom: none !important;
  padding-bottom: 0;
}
.action-separator {
  border-bottom: 1px solid #dce3ef;
  margin-top: 15px;
}
.personal-data-text {
  font-size: 10px;
  margin: 15px;
  font-style: italic;
  font-family: TitilliumWeb-Regular;
}
.divider {
  display: flex;
  align-items: center;
  margin: 20px 0;
}
.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #dce3ef;
}
.divider span {
  padding: 0 12px;
  font-family: TitilliumWeb-Regular, sans-serif;
  font-size: 13px;
  color: #48616e;
}
.method-buttons {
  display: flex;
  flex-direction: column;
  row-gap: 10px;
}
.method-buttons button {
  width: 100%;
  height: 36px;
}
</style>
